import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { DebugHudSnapshot } from '../types';

/**
 * Performance Panel
 *
 * Minimal performance readout for core engine systems.
 * Sprint 0024: expose physics performance stats.
 */
@customElement('performance-panel')
export class PerformancePanel extends LitElement {
  @state() private snapshot: DebugHudSnapshot | null = null;

  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 16px;
    }

    /* Main layout: side by side when wide enough */
    .panels-row {
      display: flex;
      flex-wrap: wrap;
      gap: 32px;
    }

    .panel-section {
      flex: 1 1 200px;
      min-width: 200px;
    }

    h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      opacity: 0.95;
    }

    .row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 8px 0;
      font-size: 12px;
    }

    .label {
      min-width: 140px;
      opacity: 0.75;
    }

    .value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 12px;
      opacity: 0.95;
    }

    /* Side by side grid for culling stats */
    .culling-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
    }

    .culling-column {
      flex: 1 1 100px;
      min-width: 100px;
    }

    .culling-column h4 {
      margin: 0 0 8px 0;
      font-size: 12px;
      font-weight: 500;
      opacity: 0.6;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .stat-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 0;
      font-size: 12px;
    }

    .culling-column .stat-label {
      min-width: 50px;
    }

    .culling-column .stat-value {
      min-width: 40px;
      text-align: right;
    }

    .stat-label {
      opacity: 0.75;
    }

    .stat-value {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      opacity: 0.95;
    }

    .stat-value.enabled {
      color: #4ade80;
    }

    .stat-value.disabled {
      color: #f87171;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
  }

  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    if (!event) return;

    switch (event.type) {
      case 'snapshot':
        this.snapshot = event.snapshot;
        return;
    }
  };

  render() {
    const fps = this.snapshot?.fps;
    const physics = this.snapshot?.physics;
    const culling = this.snapshot?.culling;

    return html`
      <div class="panels-row">
        <div class="panel-section">
          <h3>Performance</h3>
          <div class="row">
            <span class="label">FPS:</span>
            <span class="value">${fps ? Math.round(fps.current) : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Frame Time:</span>
            <span class="value">${fps ? fps.frameTimeMs.toFixed(2) + 'ms' : '—'}</span>
          </div>
        </div>
        <div class="panel-section">
          <h3>Physics</h3>
          <div class="row">
            <span class="label">Active Bodies:</span>
            <span class="value">${physics ? physics.activeBodies : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Static Bodies:</span>
            <span class="value">${physics ? physics.staticBodies : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Dynamic Bodies:</span>
            <span class="value">${physics ? physics.dynamicBodies : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Kinematic Bodies:</span>
            <span class="value">${physics ? physics.kinematicBodies : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Collision Pairs:</span>
            <span class="value">${physics ? physics.collisionChecks : '—'}</span>
          </div>
          <div class="row">
            <span class="label">Poll Time (ms):</span>
            <span class="value">${physics ? physics.frameTime.toFixed(3) : '—'}</span>
          </div>
        </div>
        
        <div class="panel-section">
          <h3>Spatial Culling</h3>
          <div class="stat-row">
            <span class="stat-label">Status</span>
            <span class="stat-value ${culling?.enabled ? 'enabled' : 'disabled'}">${culling ? (culling.enabled ? '✓ Enabled' : '✗ Disabled') : '—'}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Cull Distance</span>
            <span class="stat-value">${culling ? `${culling.cullDistance}m` : '—'}</span>
          </div>
          <div class="culling-grid">
            <div class="culling-column">
              <h4>Meshes</h4>
              <div class="stat-row">
                <span class="stat-label">Total</span>
                <span class="stat-value">${culling ? culling.totalMeshes : '—'}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Visible</span>
                <span class="stat-value">${culling ? culling.visibleMeshes : '—'}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Culled</span>
                <span class="stat-value">${culling ? culling.culledMeshes : '—'}</span>
              </div>
            </div>
            <div class="culling-column">
              <h4>Lights</h4>
              <div class="stat-row">
                <span class="stat-label">Total</span>
                <span class="stat-value">${culling ? culling.totalLights : '—'}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Visible</span>
                <span class="stat-value">${culling ? culling.visibleLights : '—'}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Culled</span>
                <span class="stat-value">${culling ? culling.culledLights : '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'performance-panel': PerformancePanel;
  }
}
